import {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAnimation } from "framer-motion";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  GetAccount200ResponsePositionsInner,
  InstrumentTypeResponse,
  OrderTypeResponse,
  SideResponse,
  Stop,
} from "../../../codegen-api";
import { AuthContext } from "../../../contexts/AuthContext";
import { ConnectWalletContext } from "../../../contexts/ConnectWalletContext";
import { useGetAccount } from "../../../hooks/api/account/useGetAccount";
import { useFarm } from "../../../hooks/api/farm/useFarm";
import { useOrder } from "../../../hooks/api/order/useOrder";
import { useOrderFormTpsl } from "../../../hooks/tpsl/useOrderFormTpsl";
import { useSpotOrder } from "../../../hooks/api/order/useSpotOrder";
import { usePerpOrder } from "../../../hooks/api/order/usePerpOrder";
import { useRFQ } from "../../../hooks/api/rfq/useRFQ";
import { useToast } from "../../../hooks/toast";
import { useSFX } from "../../../hooks/useSFX";
import usePersistentState from "../../../hooks/usePersistentState";
import useMarkPriceWSS from "../../../hooks/wss/useMarkPriceWSS";
import useOrderbookWSS from "../../../hooks/wss/useOrderbookWSS";
import usePositionsWSS from "../../../hooks/wss/usePositionsWSS";
import { AssetResponse } from "../../../utils/asset";
import { getContractPriceStep } from "../../../utils/instruments";
import { firstKnownPrice } from "../../../utils/markGuards";
import { PerpFormFieldKeyEnum, usePerpsForm } from "./form";
import { resolveSpotTokenBalances } from "./spotBalances";
import { ITradeModalProps } from "./PerpsTradeForm.types";

export function usePerpsTradeFormSetup({
  perpInstrument,
  mobileMode,
  onClose,
  defaultValues,
  tradingType = "perp",
}: PropsWithChildren<ITradeModalProps>) {
  const isSpot = tradingType === "spot";
  const isPerp = !isSpot;

  const { t: apiError } = useTranslation("apiErrors");
  const { setShowConnectModal, openDepositFlow } = useContext(ConnectWalletContext);
  const { accountSigningKeyState, account } = useContext(AuthContext);
  const { showOnboarding, data: accountData } = useGetAccount();
  const { data: farmData, mutate: mutateFarmData } = useFarm();
  const form = usePerpsForm();
  const { createOrder } = useOrder();
  const { placeSpotOrder, isLoading: isSpotLoading } = useSpotOrder();
  const { placePerpOrder, closePerpPosition, isLoading: isPerpLoading } = usePerpOrder();
  const { deleteRFQsByInstrumentId } = useRFQ(undefined, false);

  const { addToast, addErrorToast } = useToast();
  const { playSound } = useSFX();
  const animControls = useAnimation();
  const { t } = useTranslation();
  const { t: commonFormTranslations } = useTranslation("app", {
    keyPrefix: "TradeForm.Common",
  });
  const { t: perpTradeFormTranslations } = useTranslation("app", {
    keyPrefix: "TradeForm.PerpsTradeForm.PerpsTradeForm",
  });
  const { positionsMap } = usePositionsWSS();
  const [showClosePositionModal, setClosePositionModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  const { tradeUseUSDCTerms, setTradeUseUSDCTerms } = usePersistentState();


  const [orderDirection, setOrderDirection] = useState<SideResponse>(
    defaultValues?.side ?? SideResponse.Buy
  );
  const [orderType, setOrderType] = useState<OrderTypeResponse>(
    OrderTypeResponse.Market
  );
  const [stopType, setStopType] = useState<Stop>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Per-token spot balances. `available_balance` is the TOTAL portfolio value in USD and is WRONG
  // to show/validate against for spot: a BUY can only spend the quote (USDC) balance, a SELL can
  // only sell the base (e.g. BTC) balance. A balance that is not known yet is `null`, never "0"
  // (see spotBalances.ts).
  const spotTokenBalances = useMemo(
    () =>
      isSpot
        ? resolveSpotTokenBalances(accountData?.collaterals as any, perpInstrument as any)
        : undefined,
    [isSpot, accountData?.collaterals, perpInstrument]
  );

  // Direction-aware available-to-trade for the current spot side: BUY -> quote (USDC), SELL -> base.
  const spotAvailableToTrade = useMemo(() => {
    if (!isSpot || !spotTokenBalances) return undefined;
    return orderDirection === SideResponse.Sell
      ? spotTokenBalances.base
      : spotTokenBalances.quote;
  }, [isSpot, orderDirection, spotTokenBalances]);

  // A spot SELL sells base tokens, so denominate the size input in that token. In USDC terms the
  // field formats to 2dp, so 0.0001 collapsed to "0.00" and failed min-size — small balances were
  // unsellable. Only SELL is forced (a BUY spends quote); keying on side lets an explicit unit
  // change stick. Amount is cleared: "100" means very different things in USDC vs BTC.
  useEffect(() => {
    if (!isSpot || orderDirection !== SideResponse.Sell || !tradeUseUSDCTerms) {
      return;
    }
    setTradeUseUSDCTerms?.(false);
    form.setValue(PerpFormFieldKeyEnum.AMOUNT, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpot, orderDirection]);

  // Only for dropdown displaying purpose
  const [tempOrderType, setTempOrderType] = useState<OrderTypeResponse>(
    OrderTypeResponse.Market
  );
  const [tempStopType, setTempStopType] = useState<Stop>(Stop.StopLoss);
  const dropdownValue = stopType
    ? `${orderType}-${stopType}`
    : `${tempOrderType}-${tempStopType}`;

  const modalRef = useRef<HTMLDivElement>(null);

  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (modalRef.current?.clientHeight) {
        setIsSticky(modalRef.current?.clientHeight < 900);
      }
    };
    handleResize();

    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Updates default values
  useEffect(() => {
    if (defaultValues?.amount || defaultValues?.price) {
      setOrderType(OrderTypeResponse.Limit);
      if (defaultValues.amount) {
        form.setValue(PerpFormFieldKeyEnum.AMOUNT, defaultValues.amount, {
          shouldValidate: true,
        });
      }
      if (defaultValues.price) {
        form.setValue(PerpFormFieldKeyEnum.PRICE, defaultValues.price, {
          shouldValidate: true,
        });
      }
    }
  }, [defaultValues, form]);

  const currentPosition: GetAccount200ResponsePositionsInner | undefined =
    positionsMap[perpInstrument?.instrument_name || ""];

  const contractPriceStep = useMemo(
    () => getContractPriceStep(perpInstrument),
    [perpInstrument]
  );

  const { t: formTranslations } = useTranslation("app", {
    keyPrefix: "TradeForm.PerpsTradeForm.Form",
  });

  const {
    register,
    formState: { errors, isDirty, dirtyFields, isSubmitted, touchedFields },
    handleSubmit,
    setValue,
    control,
    unregister,
    trigger,
    resetField,
  } = form;

  // form fields

  const triggerPrice = useWatch({
    control,
    name: PerpFormFieldKeyEnum.TRIGGER_PRICE,
  });
  const leverage = useWatch({ control, name: PerpFormFieldKeyEnum.LEVERAGE });
  const amount = useWatch({ control, name: PerpFormFieldKeyEnum.AMOUNT });
  const price = useWatch({ control, name: PerpFormFieldKeyEnum.PRICE });
  const leverageFieldDirty = Boolean(
    dirtyFields[PerpFormFieldKeyEnum.LEVERAGE]
  );

  const showAmountValidation = Boolean(
    touchedFields[PerpFormFieldKeyEnum.AMOUNT] ||
      dirtyFields[PerpFormFieldKeyEnum.AMOUNT] ||
      isSubmitted
  );

  // wss

  // Live in both order types: a LIMIT order's Mid / Bid / Ask chips need the
  // current touch, and the REST snapshot alone can be 30 s old.
  const { orderbook: orderbookData } = useOrderbookWSS(
    perpInstrument?.instrument_name,
    OrderTypeResponse.Market
  );

  const uniqueTickers = useMemo(
    () =>
      perpInstrument
        ? {
            [perpInstrument.instrument_name]: {
              asset: perpInstrument.underlying_asset as AssetResponse,
              derivative: InstrumentTypeResponse.Perpetual,
            },
          }
        : {},
    [perpInstrument]
  );

  const { instrumentMark } = useMarkPriceWSS(uniqueTickers);

  const mark = instrumentMark[perpInstrument?.instrument_name ?? ""]; // keyed as subscribed; `${asset}-PERP` missed spot
  // Live mark, else the REST mark; `undefined` when neither is known (null /
  // omitted / "0"), so no preview, placeholder or validation runs against 0.
  const markPrice = firstKnownPrice(mark?.mark_price, perpInstrument?.mark_price);

  const [reduceOnly, setReduceOnly] = useState(false);

  // TP/SL attached to the position this order opens — state, the clear-on-uncheck
  // rule and the submit-time call all live in the hook (hooks/tpsl/useOrderFormTpsl).
  const tpsl = useOrderFormTpsl();

  return {
    perpInstrument, mobileMode, onClose, defaultValues, isSpot, isPerp,
    apiError, setShowConnectModal, openDepositFlow, accountSigningKeyState,
    account, showOnboarding, accountData, farmData, mutateFarmData, form,
    createOrder, placeSpotOrder, isSpotLoading, placePerpOrder,
    closePerpPosition, isPerpLoading, deleteRFQsByInstrumentId, addToast,
    addErrorToast, playSound, animControls, t, commonFormTranslations,
    perpTradeFormTranslations, positionsMap, showClosePositionModal,
    setClosePositionModal, showWithdrawModal, setShowWithdrawModal,
    tradeUseUSDCTerms, setTradeUseUSDCTerms,
    spotTokenBalances, spotAvailableToTrade,
    orderDirection, setOrderDirection, orderType, setOrderType, stopType,
    setStopType, isLoading, setIsLoading, tempOrderType, setTempOrderType,
    tempStopType, setTempStopType, dropdownValue, modalRef, isSticky,
    setIsSticky, currentPosition, contractPriceStep, formTranslations,
    register, errors, isDirty, dirtyFields, isSubmitted, touchedFields,
    handleSubmit, setValue, control, unregister, trigger, resetField,
    triggerPrice, leverage, amount, price, leverageFieldDirty,
    showAmountValidation, orderbookData, uniqueTickers, instrumentMark, mark,
    markPrice, reduceOnly, setReduceOnly, tpsl,
  };
}
